import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { useApiOperation } from "src/api/hooks";

import { getProviderList } from "src/api/provider/provider-api";
import { ProviderModel } from "src/api/provider/provider-types";
import { Order, PageMeta, Sort } from "src/api/public-types";
import { BaseButton, BaseInput, BaseSelect, BaseTable } from "src/components";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { qsParse } from "src/utils";
import guideListColumns from "./columns/guideListColumns";

import { getGuideListAsync } from "src/api/guide/guide-api";
import { UserGuideModel } from "src/api/guide/guide-types";
import SearchResetButton from "src/components/SearchResetButton";
import ProviderSelectModal from "src/pages/closedPeriod/components/ProviderSelectModal";
import { guideTypeOptions, userGuideTargetOptions } from "../guide-types";

// url query parameter 타입
type QueryParams = {
  page?: number;
  size?: number;
  sort?: Sort;

  idList?: string;
  providerName?: string;
  providerId?: string;
  type?: string;
  targetsList?: string; // 적용대상 (GENERAL: 이용자, VISITOR: 방문자)
  keywordList?: string;
};

/**
 * 이용안내 > 목록 화면
 */
const GuideList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams: QueryParams = useMemo(() => {
    const _queryParams: QueryParams = qsParse(location.search);

    // page, size, sort 없이 최초 진입했을때 default 값 바인딩
    if (!_queryParams?.page) {
      _queryParams.page = 0;
    }
    if (!_queryParams?.size) {
      _queryParams.size = 20;
    }
    if (!_queryParams?.providerName) {
      _queryParams.providerName = "";
    }
    if (!_queryParams?.sort) {
      _queryParams.sort = {
        orders: [{ property: "id", direction: "DESC" }],
      };
    }
    // qs.parse에 decoder때문에 검색값 입력 검색시 한번더 decoding
    if (_queryParams.keywordList) {
      _queryParams.keywordList = decodeURIComponent(_queryParams.keywordList);
    }

    return _queryParams;
  }, [location]);

  const [params, setParams] = useState<QueryParams>({ ...queryParams });
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [guideList, setGuideList] = useState<UserGuideModel[]>();
  const [isProviderModalOpen, setIsProviderModalOpen] = useState({ isOpen: false });

  const { executeAsync: getGuideList } = useApiOperation(getGuideListAsync);
  const { executeAsync: getProvider } = useApiOperation(getProviderList);

  // 쿼리파람과 함께 navigate
  const navigateWithQueryParams = useCallback(
    (passParams?: QueryParams, type?: "search" | "pagination") => {
      let data;
      if (type) {
        type === "search" ? (data = { ...params }) : (data = { ...queryParams });
      }

      const newQueryParams = { ...data, ...(passParams || {}) };
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location.pathname, params, queryParams],
  );

  const onAddedProviderSelectModal = (
    provider: {
      providerId: string;
      providerName: string;
    }[],
  ) => {
    if (!provider) return;
    setIsProviderModalOpen({ isOpen: false });
    navigateWithQueryParams({ ...params, providerName: provider[0].providerName, providerId: String(provider[0].providerId) });
  };

  useEffect(() => {
    setParams({ ...queryParams }); // input 초기화
    const fetchApi = async (guideParams: QueryParams) => {
      // 목록 조회
      let { providerName, ...payload } = guideParams;
      const response = await getGuideList(payload);
      const guideData = response.data.data.content;

      // provider목록 조회해서 providerName 매핑
      const removeDuplicateProvider = new Set(guideData.map((guide) => guide.providerId));

      const providerListString = [...removeDuplicateProvider].filter((item) => item !== null).join(",");

      const providerData: ProviderModel[] = await getProvider({ providerId: providerListString })
        .then((providerResponse) => providerResponse.data.data.content)
        .catch(() => []);

      const forColumnguideData = guideData.map((guide) => {
        const findProviderName = providerData.find((provider) => provider.providerId === guide.providerId)?.providerName;

        return { ...guide, providerName: findProviderName || "" };
      });

      setGuideList(forColumnguideData);
      setPageMeta(response.data.meta.pageMeta);
    };

    fetchApi(queryParams);
  }, [queryParams, getProvider]);

  return (
    <div className="">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            {/* section 태그가 지정검색, 조건검색, 영역이고 반드시 필요함 */}
            <section>
              <div className="left-area__index">
                <span>지정검색</span>
              </div>
              <div className="left-area__contents">
                <div className="minmax160">
                  <BaseInput
                    type="text"
                    value={params?.providerName || ""}
                    placeholder="프로바이더"
                    readonly={true}
                    onClick={(e: any) => {
                      e.preventDefault();
                      setIsProviderModalOpen({ isOpen: true });
                    }}
                    onClearClick={() => {
                      let { providerName, providerId, ...rest } = params;
                      navigateWithQueryParams({ ...rest });
                    }}
                  />
                  {isProviderModalOpen.isOpen && (
                    <ProviderSelectModal
                      onCanceled={() => setIsProviderModalOpen({ isOpen: false })}
                      onAdded={onAddedProviderSelectModal}
                      defaultChecked={params.providerName ? [{ providerId: String(params.providerId), providerName: params.providerName }] : []}
                    />
                  )}
                </div>
              </div>
            </section>
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>

              <div className="left-area__contents">
                <div className="minmax160">
                  <BaseSelect
                    placeholder="유형"
                    value={params.type}
                    stateOptions={guideTypeOptions}
                    setStateValue={(searchType: string) => {
                      setParams({ ...params, type: searchType });
                      navigateWithQueryParams({ ...params, type: searchType, page: 0 });
                    }}
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    placeholder="적용대상"
                    value={params.targetsList}
                    stateOptions={[{ value: "", label: "전체" }, ...userGuideTargetOptions]}
                    setStateValue={(targetsList: string) => {
                      setParams({ ...params, targetsList });
                      navigateWithQueryParams({ ...params, targetsList, page: 0 });
                    }}
                  />
                </div>
                <div>
                  <BaseInput
                    value={params?.keywordList || ""}
                    className="mr8"
                    type="text"
                    placeholder="항목명을 입력하세요"
                    onChange={(keywordList: string) => setParams({ ...params, keywordList })}
                    onKeyUp={() => navigateWithQueryParams({ page: 0 }, "search")}
                    onSearchClick={() => navigateWithQueryParams({ page: 0 }, "search")}
                    onClearClick={() => {
                      navigateWithQueryParams({ ...params, page: 0, keywordList: "" });
                    }}
                  />
                </div>
                <SearchResetButton />
              </div>
            </section>
          </div>
          <div className="right-area">
            <div className="flex-center">
              <BaseButton
                title="+ 항목 등록"
                onClick={() => {
                  navigate(PagePath.guide.form);
                }}
              />
            </div>
          </div>
        </div>

        {guideList && (
          <BaseTable
            data={guideList}
            columns={guideListColumns}
            pageIndex={pageMeta?.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            goPage={(page: number) => {
              navigateWithQueryParams({ page }, "pagination");
            }}
            totalElements={Number(pageMeta?.totalElements) || 0}
            orders={queryParams?.sort?.orders}
            disabledSortHeaders={["providerName", "productId", "subject", "startDate", "endTime", "targetsList"]}
            sizeOption={(number: number) => {
              setParams({ ...params, size: number });
              navigateWithQueryParams({ ...params, page: 0, size: number }, "search");
            }}
            currentSize={Number(queryParams.size) || 20}
            setOrders={(orders?: Array<Order>) => {
              if (orders) {
                navigateWithQueryParams({ ...params, sort: { orders } });
              }
            }}
          />
        )}
      </div>
    </div>
  );
};
export default GuideList;
